import type { GoodCustomsInformation, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";

const COMMODITY_FRONTEND_ONLY_FIELDS: (keyof GoodCustomsInformation)[] = [
  "commodity_key",
  "parent_assigned",
  "parent_packing_list_index",
];

function stripCommodityMetadata(commodity: GoodCustomsInformation): GoodCustomsInformation {
  const next = { ...commodity };
  COMMODITY_FRONTEND_ONLY_FIELDS.forEach((field) => {
    delete next[field];
  });
  return next;
}

/** Form indexes of the packing-list rows that are sent to the API, in payload order. */
export function getSavedPackingListRowIndexes(packingList: unknown[]): number[] {
  return packingList.flatMap((row, index) =>
    row && typeof row === "object" && !isPackingListRowEmpty(row as PackingListRow) ? [index] : [],
  );
}

export function stripUnselectedPackingListRows(payload: Record<string, unknown>): Record<string, unknown> {
  const packingList = payload.packing_list;
  if (!Array.isArray(packingList)) return payload;

  const indexMap = new Map<number, number>();
  const keptRows: PackingListRow[] = [];

  getSavedPackingListRowIndexes(packingList).forEach((oldIndex) => {
    indexMap.set(oldIndex, keptRows.length);
    keptRows.push(packingList[oldIndex] as PackingListRow);
  });

  // Commodities are stored on a single "storage" row in the form state but each carries the
  // index of the packing-list row it actually belongs to. The backend nests commodities under
  // their parent row, so redistribute each commodity to its assigned row here.
  const commodityBuckets: GoodCustomsInformation[][] = keptRows.map(() => []);

  packingList.forEach((row, oldIndex) => {
    if (!row || typeof row !== "object") return;
    const commodities = (row as PackingListRow).good_customs_information;
    if (!commodities?.length) return;

    commodities.forEach((commodity) => {
      const targetOldIndex = commodity.parent_packing_list_index ?? oldIndex;
      const targetNewIndex = indexMap.get(targetOldIndex);
      // Drop commodities whose target row was removed (orphaned assignment or empty storage row).
      if (targetNewIndex === undefined) return;

      commodityBuckets[targetNewIndex].push(stripCommodityMetadata(commodity));
    });
  });

  const remappedRows = keptRows.map((row, newIndex) => {
    const bucket = commodityBuckets[newIndex];
    if (bucket.length === 0 && !row.good_customs_information?.length) {
      return row;
    }
    return { ...row, good_customs_information: bucket };
  });

  return {
    ...payload,
    packing_list: remappedRows,
  };
}
